import { Button } from "primereact/button";
import { useAppDispatch, useAppSelector } from "../../hooks/storeHooks";
import { useCallback, useState } from "react";
import {
    filterImagesByToLoad,
    getImageCount,
    getImagesToView,
    getMissingAssetIds,
    loadAssetImages
} from "../../utils/imageHelper";
import { ItemWithImages } from "../../types/asset";
import { selectImages, selectInspectionId } from "../../store/selectors/inspectionSelectors";
import {
    setViewImages,
    updateImages
} from "../../store/slices/inspectionSlice";
import { getAssetsByIds } from "../../services/inspectionService";

type Props = {
    item: ItemWithImages;
};

export const ViewImagesButton = (props: Props) => {
    const { item } = props;

    const dispatch = useAppDispatch();

    const images = useAppSelector(selectImages);
    const inspectionId = useAppSelector(selectInspectionId);

    const [loading, setLoading] = useState(false);

    const onViewImages = useCallback(async () => {
        setLoading(true);

        let allImages = images;

        // Check if there are images referenced by the item that aren't in the pre-loaded images
        // This can happen for images uploaded from mobile app with different element-id metadata
        const missingAssetIds = getMissingAssetIds(item.images, images);

        if (missingAssetIds.length && inspectionId) {
            const fetchedAssets = await getAssetsByIds(missingAssetIds, inspectionId);

            if (fetchedAssets.length) {
                dispatch(updateImages(fetchedAssets));
                allImages = [...images, ...fetchedAssets];
            }
        }

        let viewImages = getImagesToView(item.images, allImages);

        const toLoad = filterImagesByToLoad(viewImages);

        if (toLoad.length) {
            const response = await loadAssetImages(toLoad);
            dispatch(updateImages(response));

            viewImages = viewImages.map((image) => {
                const match = response.find(
                    (responseImage) => responseImage.asset_id === image.asset_id
                );

                if (match) {
                    return match;
                }

                return image;
            });
        }

        dispatch(setViewImages(viewImages));

        setLoading(false);
    }, [item, images, inspectionId, dispatch]);

    const imagesCount = getImageCount(item.images);

    return (
        <Button
            icon={loading ? "pi pi-spin pi-spinner" : "pi pi-images"}
            rounded
            text
            onClick={onViewImages}
            tooltip="View Images"
            tooltipOptions={{ position: "top" }}
        >
            <span className="ml-3">{imagesCount}</span>
        </Button>
    );
};
